import React from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
  { code: 'ru', label: 'Русский' },
  { code: 'zh', label: '中文' }
];

interface LanguageSelectorProps {
  direction?: 'up' | 'down';
}

export const LanguageSelector = ({ direction = 'down' }: LanguageSelectorProps) => {
  const { i18n } = useTranslation();
  const [langOpen, setLangOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!langOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [langOpen]);

  // i18n.language can still carry a region ("de-DE") when it comes from a
  // saved preference written before the detector normalised codes, so compare
  // on the base subtag rather than the raw value — otherwise a German visitor
  // sees German copy while this control insists they are on English.
  const activeLang = (i18n.resolvedLanguage || i18n.language || 'en').split(
    '-'
  )[0];

  const currentLang =
    LANGUAGES.find((l) => l.code === activeLang) || LANGUAGES[0];

  const dropdownPosition =
    direction === 'up' ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]';

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setLangOpen(!langOpen)}
        className={`flex items-center gap-[6px] font-body text-[13px] text-content-muted hover:text-base-black transition-colors ${langOpen ? 'text-base-black' : ''}`}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        {currentLang.label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          className={`transition-transform ${langOpen ? 'rotate-180' : ''}`}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {langOpen && (
        <div
          className={`absolute left-0 ${dropdownPosition} z-20 min-w-[160px] bg-surface-base rounded-[12px] border border-border-dark/[0.11] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[6px] animate-fadeInMenuUp`}
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => {
                i18n.changeLanguage(lang.code);
                setLangOpen(false);
              }}
              className={`w-full flex items-center justify-between px-[14px] py-[8px] font-body text-[13px] text-content-dark hover:bg-surface-secondary transition-colors ${
                lang.code === activeLang ? 'font-semibold' : ''
              }`}
            >
              <span>{lang.label}</span>
              {lang.code === activeLang && (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--color-text-primary-alt)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
